import { create } from 'zustand'
import type { HistoryEntry } from '../types'
import {
  getHistoryEntries,
  deleteHistoryEntry as dbDeleteEntry,
  clearHistory as dbClearHistory,
  updateHistoryCopied,
} from '../lib/db'

interface HistoryState {
  entries: HistoryEntry[]
  isLoading: boolean
  searchTerm: string

  loadHistory: () => Promise<void>
  deleteEntry: (id: string) => Promise<void>
  clearHistory: () => Promise<void>
  setSearchTerm: (term: string) => void
  markCopied: (id: string, platform: string) => Promise<void>
  getFilteredEntries: () => HistoryEntry[]
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  entries: [],
  isLoading: false,
  searchTerm: '',

  loadHistory: async () => {
    set({ isLoading: true })
    try {
      const entries = await getHistoryEntries(100)
      set({ entries, isLoading: false })
    } catch (error) {
      console.error('Failed to load history:', error)
      set({ isLoading: false })
    }
  },

  deleteEntry: async (id) => {
    try {
      await dbDeleteEntry(id)
      set((state) => ({
        entries: state.entries.filter((e) => e.id !== id),
      }))
    } catch (error) {
      console.error('Failed to delete entry:', error)
    }
  },

  clearHistory: async () => {
    try {
      await dbClearHistory()
      set({ entries: [] })
    } catch (error) {
      console.error('Failed to clear history:', error)
    }
  },

  setSearchTerm: (term) => set({ searchTerm: term }),

  markCopied: async (id, platform) => {
    try {
      await updateHistoryCopied(id, platform)
      // Update local state
      set((state) => ({
        entries: state.entries.map((e) =>
          e.id === id && !e.copiedPlatforms.includes(platform as 'twitter' | 'facebook' | 'forum')
            ? { ...e, copiedPlatforms: [...e.copiedPlatforms, platform as 'twitter' | 'facebook' | 'forum'] }
            : e
        ),
      }))
    } catch (error) {
      console.error('Failed to mark copied:', error)
    }
  },

  getFilteredEntries: () => {
    const { entries, searchTerm } = get()
    if (!searchTerm.trim()) return entries

    const term = searchTerm.toLowerCase()
    return entries.filter(
      (e) =>
        e.query.toLowerCase().includes(term) ||
        e.response.tweet.toLowerCase().includes(term) ||
        e.response.facebook.toLowerCase().includes(term) ||
        e.response.forum.toLowerCase().includes(term)
    )
  },
}))
